/**
 * FloatingProgressTracker Component
 *
 * Container component that renders all active enrichment operations
 * as floating panels. Uses Portal to escape layout constraints and
 * stay visible across page navigation.
 *
 * Features:
 * - Renders via Portal (z-index: 60)
 * - Stacks multiple operations vertically
 * - Supports minimized (pill) and expanded (panel) states
 * - Fixed position at bottom-right
 *
 * Pattern follows: UserStickyPanel.tsx, RouteCalculationToast.tsx
 */

import React from 'react';
import { useShallow } from 'zustand/shallow';
import { Portal } from '../ui/Portal';
import { EnrichmentProgressPanel } from './EnrichmentProgressPanel';
import { MinimizedProgressPill } from './MinimizedProgressPill';
import {
  useEnrichmentProgressStore,
  selectOperations,
} from '../../store/enrichmentProgressStore';

// API base URL for cancellation
const getApiBaseUrl = (): string => {
  // Production: use relative URL (goes through Cloudflare proxy)
  if (typeof window !== 'undefined' && window.location.host === 'eroland.me') {
    return '';
  }
  // Development: use relative URL (Vite proxy)
  return '';
};

/**
 * FloatingProgressTracker - Container for all progress operations
 *
 * Call this once in AppShell or global provider.
 */
export const FloatingProgressTracker: React.FC = () => {
  const operations = useEnrichmentProgressStore(useShallow(selectOperations));
  const { toggleMinimized, cancelEnrichment, dismissOperation } = useEnrichmentProgressStore();

  const operationList = Object.values(operations);

  // Don't render if no operations
  if (operationList.length === 0) {
    return null;
  }

  // Cancel enrichment (calls backend + updates store)
  const handleCancel = async (uploadId: string) => {
    try {
      // Call backend cancel endpoint
      const baseUrl = getApiBaseUrl();
      await fetch(`${baseUrl}/api/houston-calendar/uploads/${uploadId}/enrich/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('[FloatingProgressTracker] Failed to cancel:', error);
    }

    // Update store state
    cancelEnrichment(uploadId);
  };

  return (
    <Portal portalId="enrichment-progress-root">
      <div
        className="fixed bottom-4 right-4 z-[60] flex flex-col gap-3 items-end"
        style={{ pointerEvents: 'none' }}
      >
        {operationList.map((operation) => (
          <div
            key={operation.id}
            style={{ pointerEvents: 'auto' }}
          >
            {operation.isMinimized ? (
              <MinimizedProgressPill
                operation={operation}
                onExpand={() => toggleMinimized(operation.id)}
              />
            ) : (
              <EnrichmentProgressPanel
                operation={operation}
                onToggleExpanded={() => {}} // Not used currently
                onToggleMinimized={() => toggleMinimized(operation.id)}
                onCancel={() => handleCancel(operation.id)}
                onDismiss={() => dismissOperation(operation.id)}
              />
            )}
          </div>
        ))}
      </div>
    </Portal>
  );
};

export default FloatingProgressTracker;
